import { useEffect } from 'react';
import { getToken } from 'firebase/messaging';
import { useAuthStore } from '../store/authStore';
import { useIsInstalledPWA } from '../utils/pwaDetection';
import { getFirebaseMessaging } from '../lib/firebase';
import { apiClient } from '../api/client';
import { isNativePlatform } from '../lib/runtime';
import analytics from '../lib/analytics';

/**
 * Requests push notification permission once the user is authenticated and the
 * app is running in standalone mode (installed on home screen).
 * On native (Capacitor), push is handled by usePushNotificationToggle instead.
 *
 * Called from AppShell so it runs for the lifetime of the app session.
 */
export function usePushNotifications(): void {
  const { isInstalled, canRequestNotifications } = useIsInstalledPWA();
  const user = useAuthStore((s) => s.user);
  const accessToken = useAuthStore((s) => s.accessToken);
  const { setPushToken } = useAuthStore((s) => ({
    setPushToken: s.setPushToken,
  }));

  useEffect(() => {
    if (!user) return;                    // wait until user is set
    if (!accessToken) return;             // wait until access token is set (fully logged in)
    if (isNativePlatform()) return;       // native push handled by Capacitor plugin
    if (!isInstalled) return;             // only prompt inside the installed PWA
    if (!canRequestNotifications) return; // no Service Worker / PushManager
    if (typeof Notification === 'undefined') return; // Web Notification API not available

    // Delay notification request until after login completes (1 second)
    const timer = setTimeout(() => {
      Notification.requestPermission().then((permission) => {
        analytics.trackPushPermission(permission as 'granted' | 'denied' | 'default');
        if (permission === 'granted') {
          registerPushToken();
        }
      });
    }, 1000);

    return () => clearTimeout(timer);
  }, [user, accessToken, isInstalled, canRequestNotifications]);

  async function registerPushToken() {
    try {
      console.log('[FCM] Starting token registration...');

      const messaging = await getFirebaseMessaging();
      if (!messaging) {
        console.warn('[FCM] Firebase messaging not supported');
        return;
      }

      console.log('[FCM] VAPID Key available:', !!import.meta.env.VITE_FIREBASE_VAPID_KEY);

      const token = await getToken(messaging, {
        vapidKey: import.meta.env.VITE_FIREBASE_VAPID_KEY,
      });

      if (!token) {
        console.warn('[FCM] Failed to get FCM token');
        return;
      }

      setPushToken(token);
      console.log('[FCM] Token obtained:', token);

      // Register token with the API
      await apiClient.post('/notifications/subscribe', {
        token,
        platform: 'web',
      });

      analytics.trackPushTokenRegistered();
      console.log('[FCM] Token registered with API');
    } catch (err) {
      console.error('[FCM] Error during registration:', err);
    }
  }
}
